import { Link, useLocation } from 'react-router';
import {
  LayoutDashboard,
  Search,
  Calendar,
  Users,
  BookOpen,
  TrendingUp,
  ChevronsUpDown,
  LogOut,
  User2,
  FileText,
  BadgeDollarSign,
  Camera,
  Landmark,
  Send,
  Check,
  CalendarClock,
  Lock,
  Settings,
  KeyRound,
  UserPen,
} from 'lucide-react';
import { useAuth } from '@/lib/use-auth';
import { useTutorProfile } from '@/lib/tutor-profile-context';
import { CosmoBudLogo } from './CosmoBudLogo';
import {
  ONBOARDING_STEPS,
  type OnboardingStepKey,
} from '@/pages/onboarding/steps';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type NavItem = { title: string; url: string; icon: React.ElementType };

const PARENT_NAV: NavItem[] = [
  { title: 'Dashboard', url: '/', icon: LayoutDashboard },
  { title: 'Find Tutors', url: '/tutors', icon: Search },
  { title: 'My Bookings', url: '/bookings', icon: Calendar },
  { title: 'Referrals', url: '/referrals', icon: Users },
];

const TUTOR_NAV: NavItem[] = [
  { title: 'Dashboard', url: '/', icon: LayoutDashboard },
  { title: 'Edit Profile', url: '/tutor/profile', icon: UserPen },
  { title: 'My Classes', url: '/tutor/bookings', icon: BookOpen },
  { title: 'Earnings', url: '/tutor/earnings', icon: TrendingUp },
];

// These tutor areas only carry data once the profile is approved & live, so
// they stay locked while the profile is still in review (or otherwise not
// published). The Dashboard always stays reachable.
const TUTOR_LOCKED_WHEN_UNPUBLISHED = new Set([
  '/tutor/profile',
  '/tutor/bookings',
  '/tutor/earnings',
]);

const STEP_ICONS: Record<OnboardingStepKey, React.ElementType> = {
  identity: User2,
  subjects: BookOpen,
  availability: CalendarClock,
  bio: FileText,
  rate: BadgeDollarSign,
  photo: Camera,
  bank: Landmark,
  review: Send,
};

function isActive(pathname: string, url: string): boolean {
  if (url === '/') return pathname === '/';
  return pathname === url || pathname.startsWith(url + '/');
}

export function AppSidebar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const tutorCtx = useTutorProfile();

  const completedSteps = tutorCtx?.profile?.completedSteps ?? [];

  // Show onboarding nav when the tutor's profile is still draft or changes_requested
  const isOnboarding =
    user?.role === 'tutor' &&
    (tutorCtx === null ||
      tutorCtx.loading ||
      tutorCtx.profile === null ||
      tutorCtx.profile.publishStatus === 'draft' ||
      tutorCtx.profile.publishStatus === 'changes_requested');

  const navItems = user?.role === 'tutor' ? TUTOR_NAV : PARENT_NAV;
  // Lock the classes/earnings tabs until the tutor's profile is published.
  const tutorAreaLocked =
    user?.role === 'tutor' &&
    tutorCtx?.profile?.publishStatus !== 'published';
  const initials = user
    ? `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase()
    : '?';
  const fullName = user ? `${user.firstName} ${user.lastName}` : '';

  return (
    <Sidebar variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/" className="gap-3">
                <CosmoBudLogo size="sm" showText={false} />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">CosmoBud</span>
                  <span className="truncate text-xs capitalize">
                    {user?.role ?? ''} Portal
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {isOnboarding ? (
          <SidebarGroup>
            <SidebarGroupLabel className="flex items-center justify-between pr-1">
              <span>Complete Your Profile</span>
              <span className="ml-auto text-[10px] font-normal tabular-nums text-muted-foreground">
                {completedSteps.length}/{ONBOARDING_STEPS.length}
              </span>
            </SidebarGroupLabel>
            <SidebarMenu>
              {ONBOARDING_STEPS.map((step) => {
                const isDone = (completedSteps as ReadonlyArray<string>).includes(step.key);
                const Icon = STEP_ICONS[step.key];
                return (
                  <SidebarMenuItem key={step.key}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(pathname, `/onboarding/${step.key}`)}
                    >
                      <Link to={`/onboarding/${step.key}`}>
                        <Icon
                          className={`shrink-0 ${isDone ? 'text-emerald-500 dark:text-emerald-400' : ''}`}
                        />
                        <span className={`flex-1 truncate ${isDone ? 'text-muted-foreground' : ''}`}>
                          {step.title}
                        </span>
                        <Check
                          className={`ml-auto h-3.5 w-3.5 shrink-0 text-emerald-500 dark:text-emerald-400 transition-opacity ${isDone ? 'opacity-100' : 'opacity-0'}`}
                        />
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ) : (
          <SidebarGroup>
            <SidebarGroupLabel>Navigation</SidebarGroupLabel>
            <SidebarMenu>
              {navItems.map((item) => {
                const locked =
                  tutorAreaLocked &&
                  TUTOR_LOCKED_WHEN_UNPUBLISHED.has(item.url);
                if (locked) {
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        disabled
                        tooltip="Unlocks once your profile is approved"
                        title="Unlocks once your profile is approved"
                        aria-disabled="true"
                        className="cursor-not-allowed opacity-50"
                      >
                        <item.icon />
                        <span>{item.title}</span>
                        <Lock className="ml-auto h-3.5 w-3.5 shrink-0" />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive(pathname, item.url)}>
                      <Link to={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-brand-600 text-white text-xs">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{fullName}</span>
                    <span className="truncate text-xs">{user?.email}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-brand-600 text-white text-xs">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{fullName}</span>
                      <span className="truncate text-xs">{user?.email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user?.role !== 'tutor' && (
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                      <Settings className="size-4" />
                      Profile & Settings
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/profile/change-password" className="flex items-center gap-2 cursor-pointer">
                    <KeyRound className="size-4" />
                    Change Password
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/logout" className="flex items-center gap-2 cursor-pointer">
                    <LogOut className="size-4" />
                    Sign out
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
